import { useQuery } from "@tanstack/react-query";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { useState } from "react";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";
import {
  IGetServiceProps,
  IGlobalMetaSchema,
  TBasicObject,
} from "@api/types/globalService.types";
import { urlUtility } from "@repo/utilities/url.utility.ts";

interface IGetAllCountriesService {
  data: TBasicObject[];
  meta: IGlobalMetaSchema;
}

export const useGetAllCountriesService = ({
  page = "1",
  per_page = "15",
  showActiveOnly = true,
  enableQuery,
}: IGetServiceProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  //Hooks
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);
  //variables
  const created_between = urlQueries["created_between"] || "";
  const allCountriesService = useQuery({
    queryKey: [
      ...CACHE_KEYS.COUNTRIES,
      debouncedQuery,
      page,
      per_page,
      showActiveOnly,
      created_between,
    ],
    queryFn: (): Promise<IGetAllCountriesService> =>
      handleGlobalGetRequestQuery({
        url: URLS.COUNTRIES,
        searchParams: {
          page,
          per_page,
          "filter[name]": debouncedQuery,
          "filter[created_between]": created_between,
          include: "currency,geo_scope",
        },
      }),
    enabled: enableQuery,
  });

  return {
    states: { searchQuery, setSearchQuery },
    services: { allCountriesService },
  };
};

export const useGetSingleCountryService = ({
  country_id,
  countryName,
}: {
  country_id?: number;
  countryName?: string;
}) => {
  const singleStateService = useQuery({
    queryKey: [...CACHE_KEYS.COUNTRIES, country_id],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.COUNTRIES}${country_id ? `/${country_id}` : ""}`,
        searchParams: {
          "filter[name]": countryName,
        },
      }),
    enabled: country_id || countryName ? true : false,
  });

  return {
    service: { singleStateService },
  };
};
